import { Handbook } from "features/handbook/model/handbook";
import {
  HANDBOOK_SCHEMAS,
  HandbookFieldKind,
} from "features/handbook/model/handbookSchema.generated";

/** Пока поле не встретилось с непустым значением, вид ссылки достоверно неизвестен. */
type FieldKind = HandbookFieldKind | "unknown";

/** Служебные поля есть у всех справочников и в таблице с формой не участвуют. */
const TECHNICAL_FIELDS = new Set([
  "id",
  "deleted",
  "editedTime",
  "createdTime",
  "serviceCodes",
  "dictionaryCode",
  "services",
]);

/** Обязательные поля, когда справочника нет в схеме: остальное проверит бэкенд. */
const FALLBACK_REQUIRED = ["nameRu", "nameKy", "code"] as const;

/**
 * Набор полей справочника заранее неизвестен. Вывести его только из пришедших
 * строк нельзя: поле со значением null и вовсе отсутствующее поле выглядят
 * одинаково, а на пустом списке полей не видно вообще. Поэтому состав берём из
 * swagger-схемы, а данные используем как уточнение — на случай, если бэкенд
 * уже ушёл вперёд схемы.
 */
type FieldMap = Record<string, FieldKind>;

function collectFields(rows: Handbook[], dictCode?: string): FieldMap {
  const schema = dictCode ? HANDBOOK_SCHEMAS[dictCode] : undefined;
  const fields: FieldMap = { ...(schema?.fields ?? {}) };

  for (const row of rows) {
    if (!row || typeof row !== "object") continue;

    for (const [name, value] of Object.entries(row)) {
      if (TECHNICAL_FIELDS.has(name)) continue;

      if (value === null || value === undefined) {
        // Поле есть, но про его вид данные ничего не говорят.
        if (!(name in fields)) fields[name] = "unknown";
        continue;
      }

      // Непустое значение — источник правды: объект это ссылка, остальное нет.
      // Так строковый `type` территориальной единицы не станет колонкой-ссылкой.
      const isRef = typeof value === "object" && !Array.isArray(value);
      fields[name] = isRef ? "ref" : "value";
    }
  }

  return fields;
}

/** Есть ли у справочника такое поле — независимо от того, заполнено ли оно. */
const hasField = (fields: FieldMap, name: string) => name in fields;

/**
 * Имя поля-ссылки: сначала точное совпадение, затем первый ключ с таким
 * суффиксом (`animalCategory`, `greenhouseCrop`). Поля, про которые точно
 * известно, что они простые значения, пропускаем.
 */
function findRefField(fields: FieldMap, name: string): string | null {
  if (name in fields && fields[name] !== "value") return name;

  const suffix = new RegExp(`${name}$`, "i");
  return (
    Object.keys(fields).find(
      (key) => suffix.test(key) && fields[key] !== "value",
    ) ?? null
  );
}

/** Какие поля показывать в таблице и в форме. */
export type HandbookFieldFlags = {
  hasAlias: boolean;
  hasBik: boolean;
  hasBank: boolean;
  hasAddress: boolean;
  hasDescriptionRu: boolean;
  hasDescriptionKy: boolean;
  hasDivision: boolean;
  hasOrder: boolean;
  hasThresholdPerM2: boolean;
  hasDataSourceStatus: boolean;
  hasCategory: boolean;
  hasCrop: boolean;
  hasRegion: boolean;
  hasType: boolean;
  hasUnitMeasurement: boolean;
  categoryFieldKey: string | null;
  cropFieldKey: string | null;
  bankFieldKey: string | null;
  regionFieldKey: string | null;
  typeFieldKey: string | null;
  unitMeasurementFieldKey: string | null;
};

export function getHandbookFieldFlags(
  rows: Handbook[],
  dictCode?: string,
): HandbookFieldFlags {
  const fields = collectFields(rows, dictCode);

  const categoryFieldKey = findRefField(fields, "category");
  const cropFieldKey = findRefField(fields, "crop");
  const bankFieldKey = findRefField(fields, "bank");
  const regionFieldKey = findRefField(fields, "region");
  const typeFieldKey = findRefField(fields, "type");
  const unitMeasurementFieldKey = findRefField(fields, "unitMeasurement");

  return {
    hasAlias: hasField(fields, "alias"),
    hasBik: hasField(fields, "bik"),
    hasBank: Boolean(bankFieldKey),
    hasAddress: hasField(fields, "address"),
    hasDescriptionRu: hasField(fields, "descriptionRu"),
    hasDescriptionKy: hasField(fields, "descriptionKy"),
    hasDivision: hasField(fields, "division"),
    hasOrder: hasField(fields, "order"),
    hasThresholdPerM2: hasField(fields, "thresholdPerM2"),
    hasDataSourceStatus: hasField(fields, "dataSourceStatus"),
    hasCategory: Boolean(categoryFieldKey),
    hasCrop: Boolean(cropFieldKey),
    hasRegion: Boolean(regionFieldKey),
    hasType: Boolean(typeFieldKey),
    hasUnitMeasurement: Boolean(unitMeasurementFieldKey),
    categoryFieldKey,
    cropFieldKey,
    bankFieldKey,
    regionFieldKey,
    typeFieldKey,
    unitMeasurementFieldKey,
  };
}

/** Обязательные поля запроса по swagger — имена такие же, как в теле запроса. */
export function getRequiredFields(dictCode?: string): readonly string[] {
  const schema = dictCode ? HANDBOOK_SCHEMAS[dictCode] : undefined;
  return schema ? schema.required : FALLBACK_REQUIRED;
}
